import React, { useState } from "react"
import styles from "./modalOutboundCampaign.module.css"
import Modal from '../index'
import { MdOutlineFileUpload } from "react-icons/md"
import ConfigSend from "./ConfigSend"
import { toast } from "react-toastify"
import ConfigMessage from "./ConfigMessage"
import ConfigContacts, { normalizeCampaignPhone } from "./ConfigContacts"
import { useWebSocketChatContext } from "../../../contexts/webSocketChatContext"
import { useEffect } from "react"
import { getMessageTemplatesByInboxId } from "../../../schemas/Chat"
import { uploadFile } from "../../../schemas/File"
import ConfigConfirmation from "./ConfigConfirmation"
import api from "../../../services/api"
import moment from "moment"
import { read, utils } from "xlsx"
import ConfigAudienceSource from "./ConfigAudienceSource"
import Button from "../../Form/Button"

const maxRows = 10000
const emptyAudienceSource = { type: "file", file: null, headers: [], rows: [] }
const normalizeString = (value = "") => String(value).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim()
const normalizeHeaders = headers => {
    const used = {}
    return headers.map((header, index) => {
        const label = header ? String(header).trim() : `col_${index}`
        const base = normalizeString(label) || `col_${index}`
        used[base] = (used[base] || 0) + 1
        return { key: used[base] === 1 ? base : `${base}_${used[base]}`, label }
    })
}
const buildCleanData = (rows, headers) => rows.slice(1).reduce((result, row) => {
    if (!row) return result
    const normalized = {}
    let hasValue = false
    headers.forEach((header, index) => {
        const value = row[index] === undefined || row[index] === null ? "" : String(row[index]).trim()
        if (value) hasValue = true
        normalized[header.key] = value
    })
    if (hasValue) result.push(normalized)
    return result
}, [])

const ModalOutboundCampaign = ({ initialConfig = {}, verifyNameIsValid = async () => { }, canUpdateCampaignLimits = false, onOpenLimitSettings = () => { }, onSubmit = () => { }, closeModal = () => { } }) => {
    const [dataConfig, setDataConfig] = useState(() => ({
        name: initialConfig?.name || "",
        description: initialConfig?.description || "",
        inbox_id: initialConfig?.inbox_id,
        update_conversations: initialConfig?.update_conversations !== false,
        when_send: "NOW",
    }))
    const [pendingMessage, setPendingMessage] = useState({})
    const [inboxes, setInboxes] = useState([])
    const [templates, setTemplates] = useState([])
    const [selectedTemplate, setSelectedTemplate] = useState({})
    const [costEstimate, setCostEstimate] = useState(null)
    const [isLoadingCostEstimate, setIsLoadingCostEstimate] = useState(false)
    const [step, setStep] = useState(1)
    const [audienceSource, setAudienceSource] = useState(emptyAudienceSource)
    const [isLoadingFile, setIsLoadingFile] = useState(false)
    const [reconciliation, setReconciliation] = useState(null)

    const dataFile = { headers: audienceSource.headers, rows: audienceSource.rows }

    const { chatInboxes } = useWebSocketChatContext()

    useEffect(() => {
        const connectedWhatsAppApiInboxes = chatInboxes.filter(inbox => inbox.platform === "whatsapp_api" && inbox.status === "connected")
        setInboxes(connectedWhatsAppApiInboxes.map(inbox => ({ id: inbox._id, value: inbox.name })))
        setDataConfig(previousValue => {
            if (!previousValue?.inbox_id || connectedWhatsAppApiInboxes.some(inbox => inbox._id === previousValue.inbox_id)) {
                return previousValue
            }
            return { ...previousValue, inbox_id: undefined, template_id: undefined }
        })
    }, [chatInboxes])

    useEffect(() => {
        if (dataConfig?.inbox_id) {
            getTemplatesByInbox(dataConfig.inbox_id)
        }
    }, [dataConfig?.inbox_id])

    useEffect(() => {
        if (dataConfig?.template_id) {
            const template = templates.find(template => template.template_id === dataConfig?.template_id)
            setSelectedTemplate(template || {})
        }
    }, [dataConfig?.template_id, templates])

    useEffect(() => {
        if (dataConfig?.template_id || !initialConfig?.template_name || !templates.length) return
        const matchingTemplate = templates.find(template => (
            template?.name === initialConfig.template_name
            && (!initialConfig.template_language || template?.language === initialConfig.template_language)
        ))
        if (matchingTemplate?.template_id) {
            setDataConfig(previousValue => ({ ...previousValue, template_id: matchingTemplate.template_id }))
        }
    }, [dataConfig?.template_id, initialConfig?.template_language, initialConfig?.template_name, templates])

    const handleChangeValueConfig = (name, value) => {
        setDataConfig(previousValue => { return { ...previousValue, [name]: value } })
    }

    const readCampaignFile = file => new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onerror = () => reject(new Error("Não foi possível ler o arquivo."))
        reader.onload = event => {
            try {
                const workbook = read(event.target.result)
                const worksheet = workbook.Sheets[workbook.SheetNames[0]]
                resolve(utils.sheet_to_json(worksheet, { header: 1 }))
            } catch (err) {
                reject(err)
            }
        }
        reader.readAsArrayBuffer(file)
    })

    const reconcileMessageMappings = (message, previousKeys, nextKeys) => {
        let preserved = 0
        let invalidated = 0
        const reconcileVariable = variable => {
            if (!variable?.value || !previousKeys.has(variable.value)) return variable
            if (nextKeys.has(variable.value)) {
                preserved += 1
                return variable
            }
            invalidated += 1
            return { ...variable, value: "" }
        }
        if (!message?.content) return { message, preserved, invalidated }
        return {
            message: {
                ...message,
                content: {
                    ...message.content,
                    header: message.content.header ? { ...message.content.header, variables: (message.content.header.variables || []).map(reconcileVariable) } : message.content.header,
                    body: message.content.body ? { ...message.content.body, variables: (message.content.body.variables || []).map(reconcileVariable) } : message.content.body,
                    buttons: (message.content.buttons || []).map(button => reconcileVariable({ ...button })),
                },
            },
            preserved,
            invalidated,
        }
    }

    const handleFileSelect = async file => {
        if (!file) {
            setAudienceSource(previous => ({ ...previous, file: null, rows: [] }))
            setReconciliation(null)
            return
        }
        const extension = file.name?.toLowerCase().split(".").pop()
        if (file.size > 2 * 1024 * 1024) return toast.error("O tamanho do arquivo não deve ultrapassar 2MB.")
        if (!["xls", "xlsx", "csv"].includes(extension)) return toast.error("Apenas arquivos .xls, .xlsx e .csv são permitidos!")

        try {
            setIsLoadingFile(true)
            const rawData = await readCampaignFile(file)
            if (!Array.isArray(rawData) || rawData.length <= 1) return toast.error("O arquivo enviado está em branco!")
            if (rawData.length > maxRows + 1) return toast.error(`Não é possível cadastrar mais que ${maxRows} contatos por vez!`)
            const headers = normalizeHeaders(rawData[0])
            const rows = buildCleanData(rawData, headers)
            if (!rows.length) return toast.error("Nenhuma linha válida encontrada no arquivo!")

            const previousKeys = new Set(audienceSource.headers.map(header => header.key))
            const nextKeys = new Set(headers.map(header => header.key))
            let preserved = 0
            let invalidated = 0
            const nextConfig = { ...dataConfig, contact_error: "" }
            for (const key of ["contact_name", "contact_phone"]) {
                if (!nextConfig[key] || !previousKeys.has(nextConfig[key])) continue
                if (nextKeys.has(nextConfig[key])) preserved += 1
                else {
                    nextConfig[key] = undefined
                    invalidated += 1
                }
            }
            const messageResult = reconcileMessageMappings(pendingMessage, previousKeys, nextKeys)
            preserved += messageResult.preserved
            invalidated += messageResult.invalidated
            setDataConfig(nextConfig)
            setPendingMessage(messageResult.message)
            setAudienceSource({ type: "file", file, headers, rows })
            setCostEstimate(null)
            if (previousKeys.size) {
                setReconciliation({ preserved, invalidated, added: headers.filter(header => !previousKeys.has(header.key)).length })
                toast.success("Planilha substituída. As demais configurações foram preservadas.")
            } else {
                setReconciliation(null)
            }
        } catch (_err) {
            toast.error("Não foi possível ler o arquivo. Verifique se ele está íntegro e tente novamente.")
        } finally {
            setIsLoadingFile(false)
        }
    }

    const handleChangeStep = async (newStep) => {
        if (newStep > step) {
            if (step > 1) {
                if (step === 2 && !audienceSource.rows.length) return toast.error("Selecione uma planilha para continuar.")
                setStep(newStep)
                return
            }
            if (!dataConfig?.name) {
                return toast.error("O nome da campanha é obrigatório!")
            }
            if (!dataConfig?.inbox_id) {
                return toast.error("A caixa de entrada é obrigatória!")
            }
            if (!inboxes.some(inbox => inbox.id === dataConfig.inbox_id)) {
                return toast.error("Selecione uma caixa WhatsApp API conectada!")
            }
            if (!dataConfig?.when_send) {
                return toast.error("O momento do envio é obrigatório!")
            }
            if (dataConfig?.when_send === "SCHEDULE" && (!dataConfig?.date_schedule_send || !dataConfig?.time_schedule_send)) {
                return toast.error("Para agendar o envio é necessário selecionar o dia e horário do agendamento!")
            }
            const nameIsValid = await verifyNameIsValid(dataConfig?.name)
            if (!nameIsValid) {
                return
            }
        }
        setStep(newStep)
    }

    const handleMessageConfigured = (message) => {
        setPendingMessage(message)
        handleChangeStep(5)
        getCampaignCostEstimate(message)
    }

    const getCampaignCostEstimate = async (message = pendingMessage) => {
        if (!dataConfig?.inbox_id) return
        const referenceDate = dataConfig?.when_send === "SCHEDULE" && dataConfig?.date_schedule_send
            ? (moment.isMoment(dataConfig.date_schedule_send) ? dataConfig.date_schedule_send.format("YYYY-MM-DD") : dataConfig.date_schedule_send)
            : new Date().toISOString()

        try {
            setIsLoadingCostEstimate(true)
            const uniquePhones = new Set((dataFile?.rows || [])
                .map(row => normalizeCampaignPhone(row?.[dataConfig?.contact_phone]))
                .filter(phone => phone.length >= 10 && phone.length <= 15))
            const response = await api.post("/outbound-campaigns/estimate-cost", {
                inbox_id: dataConfig.inbox_id,
                total_contacts: uniquePhones.size,
                category: message?.category || message?.content?.category || selectedTemplate?.category,
                reference_date: referenceDate,
            })
            setCostEstimate(response?.data?.data || null)
        } catch (err) {
            toast.error(err?.response?.data?.message)
        } finally {
            setIsLoadingCostEstimate(false)
        }
    }

    const getTemplatesByInbox = (inbox_id) => {
        getMessageTemplatesByInboxId(inbox_id).then(templates => {
            setTemplates(templates)
        }).catch(err => console.log(err))
    }

    const sendCampaign = async (message) => {
        try {
            if (message?.upload?.file) {
                const file = message.upload.file
                uploadFile(file, { resize: message?.type === "image", platform: message?.platform })
                    .then(({ keyFileS3, keyFileS3Thumb, file_id }) => {
                        message.content.keyFileS3 = keyFileS3
                        message.content.keyFileS3Thumb = keyFileS3Thumb
                        message.content.file_id = file_id
                        message.upload = undefined
                        onSubmit(dataConfig, message, dataFile)
                    })
            } else {
                onSubmit(dataConfig, message, dataFile)
            }
        } catch (err) {
            toast.error(err?.response?.data?.message)
        }
    }

    return (
        <Modal
            icon={<MdOutlineFileUpload />}
            title={["", "Configurar campanha", "Origem dos contatos", "Contatos e responsáveis", "Selecionar mensagem", "Revisar campanha"][step]}
            description={`Etapa ${step} de 5`}
            closeModal={closeModal}
        >
            <div className={styles.body}>
                <div className={styles.stepProgress} aria-label={`Etapa ${step} de 5`}>
                    {["Campanha", "Origem", "Contatos", "Mensagem", "Revisão"].map((label, index) => (
                        <div key={label} className={index + 1 === step ? styles.current : index + 1 < step ? styles.complete : ""}>
                            <i>{index + 1}</i><span>{label}</span>
                        </div>
                    ))}
                </div>
                {step > 2 && audienceSource.file && (
                    <div className={styles.audienceSourceSummary}>
                        <div><MdOutlineFileUpload /><span><strong>{audienceSource.file.name}</strong>{audienceSource.rows.length} registros · {audienceSource.headers.length} colunas</span></div>
                        <Button type="button" variant="secondary" size="sm" fullWidth={false} onClick={() => setStep(2)}>Substituir arquivo</Button>
                    </div>
                )}
                {step === 1 &&
                    <ConfigSend
                        handleChangeValue={handleChangeValueConfig}
                        handleChangeStep={handleChangeStep}
                        data={dataConfig}
                        inboxes={inboxes}
                    />
                }
                {step === 2 &&
                    <ConfigAudienceSource
                        source={audienceSource}
                        loading={isLoadingFile}
                        reconciliation={reconciliation}
                        onFileSelect={handleFileSelect}
                        handleChangeStep={handleChangeStep}
                    />
                }
                {step === 3 &&
                    <ConfigContacts
                        handleChangeValue={handleChangeValueConfig}
                        handleChangeStep={handleChangeStep}
                        data={dataConfig}
                        fields={(dataFile?.headers || [])
                            .filter(header => header?.key)
                            .map(header => ({ id: header.key, value: header.label || header.key }))}
                        rows={dataFile?.rows || []}
                    />
                }
                {step === 4 &&
                    <ConfigMessage
                        handleChangeValue={handleChangeValueConfig}
                        handleChangeStep={handleChangeStep}
                        data={dataConfig}
                        fields={(dataFile?.headers || [])
                            .filter(header => header?.key)
                            .map(header => ({ id: header.key, value: header.label || header.key }))}
                        sampleRow={dataFile?.rows?.find(row => row?.[dataConfig?.contact_phone]) || dataFile?.rows?.[0] || {}}
                        templates={templates}
                        selectedTemplate={selectedTemplate}
                        initialMessage={pendingMessage}
                        onSubmit={handleMessageConfigured}
                    />
                }
                {step === 5 &&
                    <ConfigConfirmation
                        handleChangeStep={handleChangeStep}
                        data={dataConfig}
                        dataFile={dataFile}
                        inboxes={inboxes}
                        selectedTemplate={selectedTemplate}
                        message={pendingMessage}
                        costEstimate={costEstimate}
                        isLoadingCostEstimate={isLoadingCostEstimate}
                        canUpdateCampaignLimits={canUpdateCampaignLimits}
                        onOpenLimitSettings={onOpenLimitSettings}
                        onSubmit={sendCampaign}
                    />
                }
            </div>
        </Modal>
    )
}

export default ModalOutboundCampaign
